import {useCallback, useEffect, useMemo, useState} from "react";
import {Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {ALL_BILLING_MODULE_IDS, MODULE_GROUPS} from "../feature-module-map";
import {moduleBillingService, type BillingModuleId, type ModuleSubscriptionSummary} from "../module-billing.service";
import {ModuleSubscribeDialog, ModuleSubscribeToast, useModuleSubscribe} from "./module-subscribe-dialog";

const SYMPL_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)";
const formatMoney = (value: number) => `R$ ${value.toFixed(2).replace(".", ",")}`;
const moduleGroupByModuleId = new Map(
  MODULE_GROUPS.flatMap((group) => group.variants.map((variant) => [variant.id, group.id] as const))
);

type PlanModulesSectionProps = {
  onChanged?: () => void | Promise<void>;
  disabled?: boolean;
  disabledReason?: string;
};

export const PlanModulesSection = ({onChanged, disabled = false, disabledReason}: PlanModulesSectionProps) => {
  const [summary, setSummary] = useState<ModuleSubscriptionSummary | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [cancelDialog, setCancelDialog] = useState<{
    moduleId: BillingModuleId;
    moduleName: string;
    currentPaidCount: number;
    nextPaidCount: number;
    currentMonthlyTotal: number;
    nextMonthlyTotal: number;
    currentDiscountPct: number;
    nextDiscountPct: number;
  } | null>(null);
  const [toast, setToast] = useState<{open: boolean; severity: "success" | "error"; message: string}>({
    open: false,
    severity: "success",
    message: "",
  });

  const loadSummary = useCallback(async () => {
    try {
      const data = await moduleBillingService.getSummary();
      setSummary(data);
    } catch {
      setSummary({billingAnchorDay: null, billingCycleEndsAt: null, modules: {}});
    }
  }, []);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const {dialogProps, open: openSubscribeDialog, toast: subscribeToast, onToastClose: onSubscribeToastClose} = useModuleSubscribe(
    async () => {
      await loadSummary();
      await onChanged?.();
    }
  );

  const getGroupCurrentModule = useCallback(
    (groupId: string): BillingModuleId | null => {
      if (!summary) return null;
      const group = MODULE_GROUPS.find((item) => item.id === groupId);
      if (!group) return null;
      const activeVariants = group.variants.filter((variant) => {
        const status = summary.modules[variant.id]?.status;
        return status === "active" || status === "cancel_at_period_end" || status === "pending_payment";
      });
      if (activeVariants.length === 0) return null;
      return [...activeVariants].sort((a, b) => {
        const aInStripe = summary.modules[a.id]?.inStripeSubscription ? 1 : 0;
        const bInStripe = summary.modules[b.id]?.inStripeSubscription ? 1 : 0;
        if (aInStripe !== bInStripe) return bInStripe - aInStripe;
        const aStartedAt = (summary.modules[a.id] as { startedAt?: number } | undefined)?.startedAt || 0;
        const bStartedAt = (summary.modules[b.id] as { startedAt?: number } | undefined)?.startedAt || 0;
        if (aStartedAt !== bStartedAt) return bStartedAt - aStartedAt;
        return b.monthlyPrice - a.monthlyPrice;
      })[0].id;
    },
    [summary]
  );

  const currentPaidModules = useMemo(() => {
    if (!summary) return [];
    return ALL_BILLING_MODULE_IDS.filter((id) => {
      const moduleState = summary.modules[id];
      return moduleState && moduleState.status === "active" && (moduleState.monthlyPrice || 0) > 0;
    });
  }, [summary]);

  const getDiscountPct = (paidCount: number) => (paidCount >= 4 ? 15 : paidCount === 3 ? 10 : paidCount === 2 ? 5 : 0);

  const openCancelDialog = useCallback(
    (groupId: string) => {
      if (disabled) return;
      const activeModule = getGroupCurrentModule(groupId);
      if (!activeModule || !summary) return;
      const group = MODULE_GROUPS.find((item) => item.id === groupId);
      const variant = group?.variants.find((item) => item.id === activeModule);
      const remainingPaidModules = currentPaidModules.filter((id) => id !== activeModule);
      const nextPaidCount = remainingPaidModules.length;
      const currentDiscountPct = getDiscountPct(currentPaidModules.length);
      const nextDiscountPct = getDiscountPct(nextPaidCount);
      const subtotal = currentPaidModules.reduce((total, id) => total + (summary.modules[id]?.monthlyPrice || 0), 0);
      const nextSubtotal = remainingPaidModules.reduce((total, id) => total + (summary.modules[id]?.monthlyPrice || 0), 0);
      setCancelDialog({
        moduleId: activeModule,
        moduleName: variant ? `${group?.name} · ${variant.name}` : activeModule,
        currentPaidCount: currentPaidModules.length,
        nextPaidCount,
        currentMonthlyTotal: subtotal * (1 - currentDiscountPct / 100),
        nextMonthlyTotal: nextSubtotal * (1 - nextDiscountPct / 100),
        currentDiscountPct,
        nextDiscountPct,
      });
    },
    [currentPaidModules, disabled, getGroupCurrentModule, summary]
  );

  const confirmCancelModule = useCallback(
    async (groupId: string) => {
      if (disabled) return;
      const activeModule = getGroupCurrentModule(groupId);
      if (!activeModule) return;
      setConfirming(true);
      try {
        const result = await moduleBillingService.stripeCancel(activeModule);
        await loadSummary();
        await onChanged?.();
        setToast({
          open: true,
          severity: "success",
          message: `Cancelamento agendado. Acesso até ${new Date(result.accessEndsAt).toLocaleDateString("pt-BR")}.`,
        });
      } catch (e) {
        setToast({
          open: true,
          severity: "error",
          message: e instanceof Error ? e.message : "Não foi possível cancelar o módulo.",
        });
      } finally {
        setConfirming(false);
      }
    },
    [disabled, getGroupCurrentModule, loadSummary, onChanged]
  );

  return (
    <>
      <Card elevation={0} sx={{border: 1, borderColor: "divider"}}>
        <CardContent sx={{p: {xs: 2, sm: 3}}}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{mb: 3}}>
            <Box sx={{width: 44, height: 44, borderRadius: 2, background: SYMPL_GRADIENT, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center"}}>
              <TuneIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800}>Módulos</Typography>
              <Typography variant="body2" color="text.secondary">Ative, altere variações ou cancele módulos no próximo ciclo.</Typography>
            </Box>
          </Stack>
          {disabled ? <Alert severity="warning" sx={{mb: 2}}>{disabledReason || "Há uma cobrança pendente do plano. Finalize ou cancele para alterar módulos."}</Alert> : null}

          <Grid container spacing={2}>
            {MODULE_GROUPS.map((group) => {
              const currentModuleId = getGroupCurrentModule(group.id);
              const currentVariant = group.variants.find((variant) => variant.id === currentModuleId);
              const currentState = currentModuleId ? summary?.modules[currentModuleId] : null;
              const pendingNextModuleId = currentState?.pendingNextCycleModuleId;
              const pendingNextVariant = pendingNextModuleId ? group.variants.find((variant) => variant.id === pendingNextModuleId) : null;
              const pendingCancel = currentState?.status === "cancel_at_period_end";
              const pendingModulePayment = currentState?.status === "pending_payment";
              const isActive = Boolean(currentVariant);
              const isFree = currentVariant?.monthlyPrice === 0;

              return (
                <Grid item xs={12} sm={6} md={4} key={group.id}>
                  <Card variant="outlined" sx={{height: "100%", borderRadius: 3, borderColor: isActive ? alpha("#0d9488", 0.55) : "divider", background: isActive ? `linear-gradient(160deg, ${alpha("#0d9488", 0.06)} 0%, transparent 100%)` : "background.paper"}}>
                    <CardContent sx={{p: 2.5, height: "100%", display: "flex", flexDirection: "column"}}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{mb: 1.5}}>
                        <Box sx={{width: 36, height: 36, borderRadius: 2, background: isActive ? SYMPL_GRADIENT : alpha("#94a3b8", 0.12), display: "flex", alignItems: "center", justifyContent: "center"}}>
                          {isActive ? <CheckCircleIcon sx={{fontSize: 20, color: "#fff"}} /> : <AddCircleOutlineIcon sx={{fontSize: 20, color: "text.secondary"}} />}
                        </Box>
                        {pendingCancel ? (
                          <Chip size="small" color="warning" label={`Encerra ${new Date(currentState?.accessEndsAt || currentState?.currentPeriodEnd || Date.now()).toLocaleDateString("pt-BR")}`} sx={{fontWeight: 700, fontSize: "0.68rem"}} />
                        ) : pendingModulePayment ? (
                          <Chip size="small" color="warning" label="Pagamento pendente" sx={{fontWeight: 700, fontSize: "0.68rem"}} />
                        ) : (
                          <Chip size="small" label={isActive ? (isFree ? "Grátis" : "Ativo") : "Disponível"} color={isActive ? "primary" : "default"} variant={isActive ? "filled" : "outlined"} sx={{fontWeight: 700, fontSize: "0.7rem"}} />
                        )}
                      </Stack>
                      <Typography variant="subtitle1" fontWeight={800}>{group.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, flex: 1}}>{group.description}</Typography>
                      {currentVariant ? <Box sx={{mt: 2, p: 1.25, borderRadius: 2, bgcolor: alpha("#0d9488", 0.07)}}><Typography variant="caption" fontWeight={800} color="primary.main">{currentVariant.name} · {isFree ? "R$ 0/mês" : `${formatMoney(currentVariant.monthlyPrice)}/mês`}</Typography></Box> : null}
                      {pendingNextVariant ? <Alert severity="info" variant="outlined" sx={{mt: 1.5, py: 0.5}}>Vai mudar para <strong>{pendingNextVariant.name}</strong> em {new Date(currentState?.pendingNextCycleAt || currentState?.currentPeriodEnd || Date.now()).toLocaleDateString("pt-BR")}.</Alert> : null}
                      <Stack spacing={0.75} sx={{mt: 2}}>
                        <Button variant={isActive ? "outlined" : "contained"} size="small" disabled={disabled} onClick={() => void openSubscribeDialog(group.id, summary)} sx={{fontWeight: 700, borderRadius: 2, ...(isActive ? {} : {background: SYMPL_GRADIENT})}}>
                          {pendingNextVariant ? "Alterar ou cancelar agendamento" : isActive ? "Alterar variação" : "Contratar módulo"}
                        </Button>
                        {isActive && !isFree ? (
                          <Button size="small" color="error" disabled={disabled || pendingCancel || pendingModulePayment || confirming} startIcon={<CancelOutlinedIcon sx={{fontSize: 15}} />} onClick={() => openCancelDialog(group.id)} sx={{fontWeight: 600, fontSize: "0.75rem", borderRadius: 2}}>
                            {pendingCancel ? "Cancelamento agendado" : "Cancelar no próximo ciclo"}
                          </Button>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      <Dialog open={Boolean(cancelDialog)} onClose={() => setCancelDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Cancelar módulo no próximo ciclo?</DialogTitle>
        <DialogContent dividers>
          {cancelDialog ? (
            <Stack spacing={2}>
              <Alert severity="warning">Você manterá acesso a <strong>{cancelDialog.moduleName}</strong> até o fim do ciclo atual.</Alert>
              <Typography variant="body2" color="text.secondary">
                Ao cancelar, sua assinatura passa de {cancelDialog.currentPaidCount} para {cancelDialog.nextPaidCount} módulos pagos.
                {cancelDialog.currentDiscountPct !== cancelDialog.nextDiscountPct ? <> O desconto muda de <strong>{cancelDialog.currentDiscountPct}%</strong> para <strong>{cancelDialog.nextDiscountPct}%</strong>.</> : <> O desconto permanece em <strong>{cancelDialog.nextDiscountPct}%</strong>.</>}
              </Typography>
              <Alert severity="info" variant="outlined">Mensalidade estimada após o ciclo: <strong>{formatMoney(cancelDialog.nextMonthlyTotal)}/mês</strong> (hoje: {formatMoney(cancelDialog.currentMonthlyTotal)}/mês).</Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(null)}>Manter módulo</Button>
          <Button
            color="error"
            variant="contained"
            disabled={confirming || !cancelDialog}
            onClick={() => {
              const moduleId = cancelDialog?.moduleId;
              const groupId = moduleId ? moduleGroupByModuleId.get(moduleId) : null;
              setCancelDialog(null);
              if (groupId) void confirmCancelModule(groupId);
            }}
          >
            {confirming ? "Cancelando..." : "Confirmar cancelamento"}
          </Button>
        </DialogActions>
      </Dialog>

      <ModuleSubscribeDialog {...dialogProps} />
      <ModuleSubscribeToast open={subscribeToast.open} severity={subscribeToast.severity} message={subscribeToast.message} onClose={onSubscribeToastClose} />
      <ModuleSubscribeToast open={toast.open} severity={toast.severity} message={toast.message} onClose={() => setToast((current) => ({...current, open: false}))} />
    </>
  );
};
